import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const TopFoodItemsChart = ({ orders = [] }) => {
  // 🔥 build full dataset
  const { topItems, totalSold } = useMemo(() => {
    const counts = {};

    orders.forEach((order) => {
      order.orderItems?.forEach((item) => {
        const title = item.title;
        if (!title) return;

        counts[title] = (counts[title] || 0) + (item.qty || 1);
      });
    });

    // ✅ total sold (ALL items)
    const total = Object.values(counts).reduce((sum, val) => sum + val, 0);

    // ✅ top 10 only for chart
    const top = Object.entries(counts)
      .map(([title, count]) => ({ title, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return { topItems: top, totalSold: total };
  }, [orders]);

  const topItem = topItems[0];

  // ✂️ wrap long names
  const CustomXAxisTick = ({ x, y, payload }) => {
    const words =
      payload.value.length > 14
        ? payload.value.match(/.{1,14}/g)
        : [payload.value];

    return (
      <g transform={`translate(${x},${y})`}>
        {words.map((word, index) => (
          <text
            key={index}
            x={0}
            y={index * 12}
            textAnchor="middle"
            fill="#fed693"
            fontSize={10}
          >
            {word}
          </text>
        ))}
      </g>
    );
  };

  return (
    <div className="rounded-2xl border border-navy shadow-sm p-6 min-h-[500px]">
      {/* 🔥 HEADER */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-gray-400">
            Top 10 Food Items
          </p>

          <div className="flex items-end gap-3 mt-2">
            <h2 className="text-4xl font-bold text-gray-900">{totalSold}</h2>
            <span className="text-sm text-gray-400 pb-1">items sold</span>
          </div>

          <p className="text-sm text-gray-400 mt-1">
            {topItem ? `${topItem.title} • ${topItem.count} sold` : "No data"}
          </p>
        </div>

        <div className="px-3 py-1 text-xs rounded-full bg-green-50 text-green-600 font-medium">
          Live
        </div>
      </div>

      {/* 🔥 CHART */}
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={topItems}
            margin={{ top: 10, right: 20, bottom: 30, left: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="title"
              interval={0}
              angle={-25}
              textAnchor="end"
              height={70}
              tickFormatter={(value) =>
                value.length > 14 ? value.slice(0, 14) + "..." : value
              }
              tick={{ fontSize: 10 }}
              stroke="#132861"
            />

            <YAxis stroke="#132861" />

            <Tooltip formatter={(value) => [`${value} sold`, "Qty"]} />

            <Line
              type="monotone"
              dataKey="count"
              stroke="#132861"
              strokeWidth={2}
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-6 rounded-xl border border-gray-300 overflow-hidden">
        {/* TABLE */}
        <div className="max-h-[260px] overflow-y-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-navy z-10 ">
              <tr className="text-left text-xs text-yellowstar uppercase tracking-wider border-b border-navy">
                <th className="px-4 py-2 w-10">#</th>
                <th className="px-4 py-2">Item</th>
                <th className="px-4 py-2 text-right">Qty</th>
              </tr>
            </thead>

            <tbody>
              {topItems.map((item, index) => (
                <tr
                  key={item.title}
                  className="border-b last:border-none hover:bg-drylight transition"
                >
                  {/* RANK */}
                  <td className="px-4 py-2 text-gray-400">{index + 1}</td>

                  {/* NAME */}
                  <td className="px-4 py-2 font-medium text-gray-900 truncate max-w-[180px]">
                    {item.title}
                  </td>

                  {/* COUNT */}
                  <td className="px-4 py-2 text-right font-semibold text-gray-700">
                    {item.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TopFoodItemsChart;
